<template>
	<div class="best-price-calendar">
		<div class="best-price-calendar__month-wrapper">
			<div class="best-price-calendar__month-left">
				<pager
					:disabled="visibleMonthIndex === 0 || loading"
					direction="left"
					aria-label="Vorheriger Monat"
					class="best-price-calendar__pager is-prev"
					@slide="onSlide"
				/>
				<transition-group
					tag="span"
					name="transition-month-change"
					class="best-price-calendar__month-name"
				>
					<template v-for="(month, index) in monthsToShow">
						<span
							v-if="month.month + (month.year - currentYear) * 12 === visibleMonthIndex + currentMonth"
							:key="index"
						>
							{{ getMonthString(month.month) }} {{ month.year }}
						</span>
					</template>
				</transition-group>
			</div>
			<div class="best-price-calendar__month-right">
				<transition-group
					tag="span"
					name="transition-month-change"
					class="best-price-calendar__month-name"
				>
					<template v-for="(month, index) in monthsToShow">
						<span
							v-if="month.month + (month.year - currentYear) * 12 === visibleMonthIndex + currentMonth + 1"
							:key="index"
						>
							{{ getMonthString(month.month) }} {{ month.year }}
						</span>
					</template>
				</transition-group>
			</div>
			<pager
				:disabled="visibleMonthIndex === monthsToShow.length - (isMinTablet ? 2 : 1) || loading"
				direction="right"
				aria-label="Nächster Monat"
				class="best-price-calendar__pager is-next"
				@slide="onSlide"
			/>
		</div>
		<div class="best-price-calendar__wrap">
			<div
				ref="swiper"
				class="best-price-calendar__inner"
				:style="calendarTransform"
			>
				<Month
					v-for="(monthData, index) in monthsToShow"
					:key="index"
					ref="monthComponent"
					v-observe-visibility="{
						callback: (isVisible: boolean) => {
							isVisible && visibilityChanged(monthData, index);
						},
						once: true,
					}"
					v-bind="monthData"
					class="best-price-calendar__month"
					:class="{ 'active-calendar': monthData.active }"
					:selection="model"
					:horizontal="true"
					:offers="visibleOffers"
					:best-price-calendar="true"
					:cheapest-offer="cheapestOffer"
					@select="onSelect"
				/>
			</div>

			<div class="best-price-calendar__price-display-wrapper">
				<p class="best-price-calendar__price-display">{{ PRICE_DISPLAY }}</p>
				<p class="best-price-calendar__cheapest-offer">{{ CHEAPEST_OFFER }}</p>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ObserveVisibility as vObserveVisibility } from 'vue-observe-visibility';
import { now, formatDate, dateDiff } from '@/js/utils/dateTimeUtils';
import type { SelectionType } from '@/components/common/types';
import type { BestAccommodationOfferForCheckInDate } from '@/interfaces/api/v1-best-accommodation-offer-for-check-in-date';
import { useSwipe } from '@vueuse/core';
import type { SwipeDirection } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from '@/components/common/store';
import type { MonthData } from '@helper/types/commonProps';
import Pager from '../Pager/Pager.vue';
import Month from '../Month/Month.vue';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

interface Props {
	maxMonths?: number;
	offers: BestAccommodationOfferForCheckInDate[];
	startDate: Date;
	loading: boolean;
}

const PRICE_DISPLAY = 'Preisdarstellung: Gesamtpreis';
const CHEAPEST_OFFER = '= Günstigstes Angebot';

const props = withDefaults(defineProps<Props>(), {
	maxMonths: 24,
});

const selection = ref<SelectionType>({
	from: 0,
	to: 0,
});

const store = useStore();

const visibleOffers = ref<BestAccommodationOfferForCheckInDate[]>([]);

const nowDate = ref<Date>(now() as Date);

const currentMonth = ref(nowDate.value.getMonth());

const currentYear = ref<number>(nowDate.value.getFullYear());

const monthsToShow = ref<MonthData[]>([]);

const visibleMonthIndex = ref(Math.max(0, dateDiff(nowDate.value, props.startDate, 'month')));

const calendarTransform = ref<Record<string, string>>({ transform: 'translateX(0%)' });

const { isMinTablet } = storeToRefs(useBreakpointStore());

const createRange = (start: number, length: number): number[] => Array.from({ length }, (_value, index) => index + start);

const getInitialMonthNumbers = (): number[] => {
	let untilMonth = visibleMonthIndex.value;
	if (isMinTablet.value) {
		untilMonth++;
	}

	return createRange(currentMonth.value, untilMonth + 1);
};

const updateCalendarScrollPosition = (): void => {
	const scrollHorizontal = visibleMonthIndex.value * -(isMinTablet.value ? 50 : 100);
	calendarTransform.value = {
		transform: `translateX(${scrollHorizontal}%)`,
	};
};
const emit = defineEmits(['selectDay', 'changeMonth', 'input']);

const model = computed<SelectionType>({
	get() {
		const filter = store.getters.filter;
		return { from: +new Date(filter), to: +new Date(filter) };
	},
	set(newValue) {
		selection.value = newValue;
		emit('input', newValue);
	},
});

const onSelect = (day: Date) => {
	const date = day.getTime();
	model.value.from = date;
	model.value.to = date;
	emit('selectDay', day.toString());
};

const clearSelection = () => {
	model.value.from = 0;
	model.value.to = 0;
};

const emitChangeMonthEvent = (direction: number) => {
	let addToIndex;
	if (direction > 0) {
		addToIndex = isMinTablet.value ? 2 : 1;
	} else {
		addToIndex = -2;
	}
	const monthToShowEmitIndex = visibleMonthIndex.value + addToIndex;
	if (monthToShowEmitIndex >= 0) {
		emit('changeMonth', monthsToShow.value[monthToShowEmitIndex]);
	}
};

const filterVisibleOffers = (monthData: MonthData) => {
	visibleOffers.value.push(
		...props.offers.filter((offer) => {
			const offerDate = new Date(offer.StartDate);
			return offerDate.getMonth() === monthData.month && offerDate.getFullYear() === monthData.year;
		})
	);
};

const cheapestOffer = computed((): number | undefined => {
	if (visibleOffers.value.length) {
		const prices = visibleOffers.value.map((offer) => offer.Price);
		return Math.round(Math.min(...prices));
	}
	return undefined;
});

const setMonthActive = (index: number) => {
	const month = monthsToShow.value[index];
	if (month) {
		month.active = true;
		filterVisibleOffers(month);
	}
};

const updateVisibleMonths = () => {
	monthsToShow.value.forEach((month) => {
		month.active = false;
	});
	visibleOffers.value = [];
	setMonthActive(visibleMonthIndex.value);
	if (isMinTablet.value) {
		setMonthActive(visibleMonthIndex.value + 1);
	}
};

const onSlide = (direction: number) => {
	emitChangeMonthEvent(direction);
	visibleMonthIndex.value = Math.max(0, Math.min(visibleMonthIndex.value + direction, monthsToShow.value.length));
	updateCalendarScrollPosition();
	updateVisibleMonths();
};

const getMonth = (offset: number): MonthData => {
	const date = new Date(currentYear.value, offset);
	return {
		year: date.getFullYear(),
		month: date.getMonth(),
		offset,
		active: false,
	};
};

const createInitialMonthToShow = () => {
	const monthNumbers = getInitialMonthNumbers();
	monthsToShow.value = monthNumbers.map((number) => getMonth(number));
	monthsToShow.value.forEach((month) => {
		month.active = true;
		filterVisibleOffers(month);
	});
};

const getMonthString = (index: number): string => {
	const monthName = formatDate(new Date(2000, index, 1), { month: 'long' });
	return `${monthName}`;
};

const visibilityChanged = (month: MonthData, index: number) => {
	// lookahead 2 visible month
	const count = monthsToShow.value.length;
	if (index + 2 > count && count < props.maxMonths) {
		const next = getMonth(month.offset + 1);
		monthsToShow.value.push(next);
	}
};

const swiper = ref<HTMLElement | null>(null);

useSwipe(swiper, {
	onSwipeEnd(e: TouchEvent, direction: SwipeDirection) {
		if (direction === 'RIGHT') onSlide(-1);
		if (direction === 'LEFT') onSlide(1);
	},
});

watch(isMinTablet, () => {
	updateCalendarScrollPosition();
	updateVisibleMonths();
});

onMounted(() => {
	createInitialMonthToShow();
	updateCalendarScrollPosition();
	updateVisibleMonths();
});

defineExpose({
	clearSelection,
});
</script>

<style lang="scss" scoped>
.best-price-calendar {
	position: relative;
	height: 100%;

	&__price-display-wrapper {
		display: flex;
		justify-content: space-between;
		margin-bottom: 1.6rem;
		margin-left: 2.5rem;
		font-size: $font-small-2;
		font-weight: $font-weight-semibold;
		flex-direction: column;
		gap: 0.8rem;

		@media (min-width: $breakpoint-small) {
			font-size: $font-medium-3;
			flex-direction: row;
			margin-inline: 1.6rem;
		}
	}

	&__cheapest-offer {
		position: relative;
		margin-left: 2.5rem;

		@media (min-width: $breakpoint-small) {
			margin-left: 0;
		}
	}

	&__cheapest-offer::before {
		content: '';
		position: absolute;
		top: 0;
		left: -2.5rem;
		width: 0;
		height: 0;
		border-right: 2.2rem solid transparent;
		border-color: transparent;
		transition: border-top 0.25s;
		transition-delay: 0.3s;
		border-top: 2.2rem solid $color-extra;
	}

	&__wrap {
		background: white;
		border-radius: $border-radius-small;
		overflow: hidden;
		max-width: none;
	}

	&__inner {
		display: flex;
		flex-direction: row;
		width: 100%;
		transition: transform ease-out 0.25s;
		gap: 1rem;
	}

	&__month {
		min-width: calc(100% - 1rem);
		transition: min-width ease-out 0.25s;
		user-select: none;
		height: 38.4rem;

		&.active-calendar {
			min-width: calc(100%);
		}
	}

	@media (min-width: $breakpoint-small) {
		&__month {
			min-width: calc(50% - 1rem);

			&.active-calendar {
				min-width: calc(50%);
			}
		}
	}

	&__pager.is-next {
		position: absolute;
		right: 0;
	}

	&__pager.is-prev {
		position: absolute;
		left: 0;
	}

	&__month-wrapper {
		top: 0;
		padding: 0;
		font-size: $font-medium-3;
		font-weight: $font-weight-semibold;
		margin-top: 1.8rem;
		margin-bottom: 2.5rem;
		display: flex;
		align-items: center;
		gap: 1rem;
		position: relative;
	}

	&__month-name {
		position: relative;
		top: 3px;
		width: 300px;
		height: 32px;
		display: flex;
		justify-content: center;

		span {
			position: absolute;
		}
	}

	&__month-left,
	&__month-right {
		display: flex;
		justify-content: center;
		width: 100%;
		transition: width 0.25s ease-out;
		border-radius: $border-radius-large;
		background: $color-vivid;

		@media (min-width: $breakpoint-small) {
			width: 50%;
		}
	}

	&__month-right {
		display: none;

		@media (min-width: $breakpoint-small) {
			display: flex;
		}
	}
}

.transition-month-change-enter-active,
.transition-month-change-leave-active {
	transition: opacity 0.15s;
}

.transition-month-change-enter-active {
	transition-delay: 0.15s;
}

.transition-month-change-enter-from,
.transition-month-change-leave-to {
	opacity: 0;
}
</style>
